import React, { useEffect, useMemo } from 'react';
import ButtonPrimary from '../../../../../Common/Button/Primary/ButtonPrimary';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Input from '../../../../../Common/Input/Input';
import InputPassword from '../../../../../Common/Input/InputPassword';
import SelectCommon from '../../../../../Common/Select/SelectCommon';
import styles from './SignUpForm.module.scss';
import { useAppDispatch, useAppSelector } from '../../../../../../config/redux/hooks';
import { flushRequestData, saveEmail, signup } from '../../Steps/MainForm/signupMainFormSlice';
import useNotifyOnError from '../../../../../../common/form/useNotifyOnError';

const options = [
  { value: 'signup.main.form.survey.search', label: 'Search engine (Google, Bing, etc.)' },
  { value: 'signup.main.form.survey.socials', label: 'Social Media (Facebook, Twitter, etc.)' },
  { value: 'signup.main.form.survey.email', label: 'Contacted via Email' },
  { value: 'signup.main.form.survey.phone', label: 'Contacted via Phone call' },
  { value: 'signup.main.form.survey.article', label: 'Article in a magazine' },
  { value: 'signup.main.form.survey.blog', label: 'Article on a website blog' },
  { value: 'signup.main.form.survey.someone', label: 'Recommended by someone I know' },
  { value: 'signup.main.form.survey.youtube', label: 'YouTube or Vimeo' },
  { value: 'signup.main.form.survey.other', label: 'Other' },
];

function SignUpForm() {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const dispatch = useAppDispatch();
  const waitingForCode = useAppSelector(state => state.auth.signup.main.waitingForCode);
  const error = useAppSelector(state => state.auth.signup.main.error);
  const surveyOptions = useMemo(() => options.map(it => ({ ...it, label: t(it.value) })), [t]);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      password: '',
      howDidYouHearAboutUs: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required(t('common.form.validation.required')),
      lastName: Yup.string().required(t('common.form.validation.required')),
      companyName: Yup.string(),
      email: Yup.string().email(t('common.form.validation.email')).required(t('common.form.validation.required')),
      password: Yup.string()
        .required(t('common.form.validation.password.required'))
        .min(6, t('common.form.validation.password.short'))
        .max(256, t('common.form.validation.password.long'))
        .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[ -/:-@\\[-`{-~]).{6,64}$/, t('common.form.validation.password.letters')),
      howDidYouHearAboutUs: Yup.string().required(t('common.form.validation.required')),
    }),
    onSubmit: values => {
      dispatch(saveEmail(values.email));
      dispatch(signup(values));
    },
  });

  useEffect(() => {
    if (waitingForCode) {
      navigation('/auth/signup/confirm');
      dispatch(flushRequestData());
    }
  }, [waitingForCode]);

  useNotifyOnError(error);

  return (
    <form onSubmit={formik.handleSubmit} className={styles.form}>
      <div className={styles.row}>
        <Input
          label={t('signup.main.form.label.first_name')}
          id={'firstName'}
          name={'firstName'}
          type={'firstName'}
          placeholder={t('signup.main.form.placeholder.first_name')}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.firstName}
          touched={formik.touched.firstName}
          error={formik.errors.firstName}
        />
        <div className={styles.divider} />
        <Input
          label={t('signup.main.form.label.last_name')}
          id={'lastName'}
          name={'lastName'}
          type={'lastName'}
          placeholder={t('signup.main.form.placeholder.last_name')}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.lastName}
          touched={formik.touched.lastName}
          error={formik.errors.lastName}
        />
      </div>
      <Input
        label={t('signup.main.form.label.company')}
        id={'companyName'}
        name={'companyName'}
        placeholder={t('signup.main.form.placeholder.company')}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.companyName}
        touched={formik.touched.companyName}
        error={formik.errors.companyName}
      />
      <Input
        label={t('common.form.label.email')}
        id={'email'}
        name={'email'}
        type={'email'}
        placeholder={t('common.form.placeholder.email')}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.email}
        touched={formik.touched.email}
        error={formik.errors.email}
      />
      <InputPassword
        label={t('common.form.label.password')}
        id={'password'}
        name={'password'}
        placeholder={t('common.form.placeholder.password')}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.password}
        touched={formik.touched.password}
        error={formik.errors.password}
        info={t('signup.main.form.password.info')}
      />
      <SelectCommon
        id={'howDidYouHearAboutUs'}
        name={'howDidYouHearAboutUs'}
        touched={formik.touched.howDidYouHearAboutUs}
        error={formik.errors.howDidYouHearAboutUs}
        placeholder={t('signup.main.form.survey.placeholder')}
        options={surveyOptions}
        onChangeAction={v => formik.setFieldValue('howDidYouHearAboutUs', v)}
      />
      <ButtonPrimary type={'submit'} label={t('signup.main.form.button.next')} />
    </form>
  );
}

export default SignUpForm;
